import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  PageSize,
  QueueSort,
  ReviewQueueParams,
  ReviewStatusFilter,
  useAppSelector,
  useGetJobDetailQuery,
  useGetReviewQueueQuery,
  ValidationError,
} from 'api';
import { PageContainer } from 'app';
import { QueueFilters } from './components/QueueFilters';
import { QueueList } from './components/QueueList';
import { FieldReviewPanel } from './components/FieldReviewPanel';
import { SourceTextPane } from './components/SourceTextPane';
import { PromptPanel } from './components/PromptPanel';
import { ConfidenceIndicator } from './components/ConfidenceIndicator';
import { QaResultIndicator } from './components/QaResultIndicator';
import { SubmitBar } from './components/SubmitBar';
import {
  FieldDecisionState,
  useReviewDecisions,
} from './hooks/useReviewDecisions';

export const DocumentReviewPage = () => {
  const { currentMenuItem } = useAppSelector(state => state.menuItem);
  const { mockUser } = useAppSelector(state => state.appUser);
  const uid = mockUser?.id.toString() ?? '';
  const title =
    currentMenuItem && currentMenuItem.id !== -1
      ? currentMenuItem.text
      : 'AI Entity Review';

  // --- Queue filters, sort and paging --------------------------------------
  const [reviewStatus, setReviewStatus] = useState<ReviewStatusFilter>('All');
  const [extractionTypeKey, setExtractionTypeKey] = useState<number | null>(
    null
  );
  const [mrnInput, setMrnInput] = useState('');
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [sort, setSort] = useState<QueueSort>('dateDesc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [selectedJobKey, setSelectedJobKey] = useState<number | null>(null);
  const [activeFieldName, setActiveFieldName] = useState<string | null>(null);
  // The field armed for "Select text reference"; the source pane takes the next selection.
  const [selectingFieldName, setSelectingFieldName] = useState<string | null>(
    null
  );
  // Fields named in the last submit rejection, so their panels can be expanded.
  const [submitErrorFieldNames, setSubmitErrorFieldNames] = useState<string[]>(
    []
  );

  const handleValidationErrors = (errors: ValidationError[]) =>
    setSubmitErrorFieldNames(
      errors
        .map(e => e.field)
        .filter((field): field is string => field != null)
    );

  const mrn = mrnInput.trim() !== '' ? mrnInput.trim() : null;

  // Changing a filter or the sort goes back to the first page.
  useEffect(() => {
    setPage(1);
  }, [reviewStatus, extractionTypeKey, mrn, clientKey, sort]);

  const queueParams: ReviewQueueParams & { uid: string } = useMemo(
    () => ({
      reviewStatus,
      extractionTypeKey,
      mrn,
      clientKey,
      sort,
      page,
      pageSize,
      uid,
    }),
    [reviewStatus, extractionTypeKey, mrn, clientKey, sort, page, pageSize, uid]
  );

  // Without isError, a failed queue call looks exactly like an empty queue.
  const {
    data: queueData,
    isFetching: queueFetching,
    isError: queueError,
  } = useGetReviewQueueQuery(queueParams, { skip: !uid });

  const rows = queueData?.rows ?? [];
  const totalCount = queueData?.totalCount ?? 0;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  const handleSelectJob = (jobKey: number | null) => {
    setSelectedJobKey(jobKey);
    setActiveFieldName(null);
    setSelectingFieldName(null);
  };

  // Drop the selection when a filter, sort or page change moves the job out of view.
  useEffect(() => {
    if (queueFetching || selectedJobKey == null) return;
    if (!rows.some(row => row.extractionJobKey === selectedJobKey)) {
      handleSelectJob(null);
    }
  }, [rows, queueFetching, selectedJobKey]);

  const {
    data: jobDetail,
    isFetching: detailFetching,
    isError: detailError,
    error: detailErrorDetail,
  } = useGetJobDetailQuery(
    { jobKey: selectedJobKey ?? 0, uid },
    { skip: selectedJobKey == null || !uid }
  );
  // A 404 means superseded or outside the caller's practices; anything else is a real failure.
  const detailNotFound =
    detailError &&
    typeof detailErrorDetail === 'object' &&
    detailErrorDetail !== null &&
    'status' in detailErrorDetail &&
    (detailErrorDetail as { status?: unknown }).status === 404;

  const { decisions, setDecision, addFieldReference, approveAll, undecidedCount } =
    useReviewDecisions(jobDetail);

  const activeField =
    jobDetail?.fields.find(f => f.fieldName === activeFieldName) ?? null;

  // Arming a field also makes it active, so its model citations show while the
  // reviewer picks a reference. Clicking the same button again disarms it.
  const handleToggleSelectReference = (fieldName: string) => {
    setActiveFieldName(fieldName);
    setSelectingFieldName(prev => (prev === fieldName ? null : fieldName));
  };

  // Each selection adds one reference; arm again to add another.
  const handleTextSelected = (start: number, stop: number, selectedText: string) => {
    if (!selectingFieldName) return;
    addFieldReference(selectingFieldName, { start, stop, selectedText });
    setSelectingFieldName(null);
  };

  // References survive a Correct/Incorrect toggle. Only Should be blank, which
  // carries none, disarms a pending selection.
  const handleDecisionChange = (
    fieldName: string,
    patch: Partial<FieldDecisionState>
  ) => {
    setDecision(fieldName, patch);
    if (selectingFieldName === fieldName && patch.shouldBeBlank === true) {
      setSelectingFieldName(null);
    }
  };

  const overrideRanges =
    (activeFieldName && decisions[activeFieldName]?.citationOverrides) || [];

  return (
    <PageContainer
      sx={{
        // A fixed height so the three columns scroll on their own instead of the
        // whole page. 160px is roughly the top bar, this margin and the footer.
        height: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">{title}</Typography>
        <PromptPanel
          prompt={jobDetail?.extractionPrompt ?? null}
          jobSelected={selectedJobKey != null && !!jobDetail}
        />
      </Box>

      <QueueFilters
        reviewStatus={reviewStatus}
        onReviewStatusChange={setReviewStatus}
        extractionTypeKey={extractionTypeKey}
        onExtractionTypeKeyChange={setExtractionTypeKey}
        mrnInput={mrnInput}
        onMrnInputChange={setMrnInput}
        clientKey={clientKey}
        onClientKeyChange={setClientKey}
        sort={sort}
        onSortChange={setSort}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Left column — job queue. Scrolls independently. */}
        <Box sx={{ width: '32%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            {totalCount === 0
              ? '0 of 0'
              : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
          </Typography>
          {queueError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              The queue couldn't be loaded. This is an error, not an empty
              queue. Refresh the page to try again.
            </Alert>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <QueueList
              rows={rows}
              totalCount={totalCount}
              loading={queueFetching}
              page={page}
              pageSize={pageSize}
              onPaginationChange={setPage}
              selectedJobKey={selectedJobKey}
              onSelectJob={handleSelectJob}
            />
          </Box>
        </Box>

        {/* Middle column — field review. Scrolls with the field list. */}
        <Box sx={{ width: '36%', overflow: 'auto', minHeight: 0 }}>
          {selectedJobKey == null ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Select a job from the queue to review its fields.
            </Typography>
          ) : detailNotFound ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              This job could not be loaded. It may have been superseded by a
              newer extraction, or it's outside your accessible practices.
            </Alert>
          ) : detailError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Something went wrong loading this job. Refresh the page to try
              again.
            </Alert>
          ) : detailFetching || !jobDetail ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Loading job…
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 1 }}>
                <ConfidenceIndicator value={jobDetail.ocrConfidence} />
                <QaResultIndicator
                  qaPassed={jobDetail.qaPassed}
                  qaRationale={jobDetail.qaRationale}
                />
              </Box>

              {jobDetail.previousReview && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Previously reviewed by{' '}
                  {jobDetail.previousReview.reviewedByName ?? 'Unknown reviewer'}{' '}
                  on{' '}
                  {dayjs(jobDetail.previousReview.reviewedAt).format(
                    'MMM D, YYYY'
                  )}
                </Alert>
              )}

              <FieldReviewPanel
                jobDetail={jobDetail}
                decisions={decisions}
                onDecisionChange={handleDecisionChange}
                activeFieldName={activeFieldName}
                onSelectField={setActiveFieldName}
                undecidedCount={undecidedCount}
                onApproveAll={approveAll}
                selectingFieldName={selectingFieldName}
                onToggleSelectReference={handleToggleSelectReference}
                errorFieldNames={submitErrorFieldNames}
              />

              <SubmitBar
                jobDetail={jobDetail}
                decisions={decisions}
                onValidationErrors={handleValidationErrors}
              />
            </>
          )}
        </Box>

        {/* Right column — source text with citation highlighting. Scrolls
            independently. */}
        <Box sx={{ width: '32%', minHeight: 0, overflow: 'hidden' }}>
          {selectedJobKey == null ? (
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography color="text.secondary">
                Source text will appear here once a job is selected.
              </Typography>
            </Paper>
          ) : jobDetail ? (
            <SourceTextPane
              documentText={jobDetail.documentText}
              ocrError={jobDetail.ocrError}
              citations={jobDetail.citations}
              activeField={activeField}
              selecting={selectingFieldName != null}
              overrideRanges={overrideRanges}
              onSelectText={handleTextSelected}
            />
          ) : null}
        </Box>
      </Box>
    </PageContainer>
  );
};
